import ReactDOM from "react-dom/client";
import { Redirect, Route, Switch } from "wouter";
import LoginPage from "./app/auth/(login)/login/page";
import LoginLayout from "./app/auth/(login)/layout";
import ForgotPasswordPage from "./app/auth/(login)/forgot-password/page";
import ResetEmailSentPage from "./app/auth/(login)/reset-email-sent/page";
import ResetPasswordPage from "./app/auth/(login)/reset-password/page";
import PaymentInformationPage from "./app/auth/(signup)/payment-information/page";
import SignupPage from "./app/auth/(signup)/signup/page";
import ConnectSellerApiPage from "./app/connect/seller-api/page";
import SellerApiConnectSucceedPage from "./app/connect/seller-api/succeed/page";
import AssistantPage from "./app/auth/(signup)/assistants/page";
import AdminLayout from "./app/admin/AdminLayout";
import AdminUsersPage from "./app/admin/AdminUsersPage";
import AdminDashboardPage from "./app/admin/AdminDashboardPage"
import DashboardLayout from "./app/dashboard/DashboardLayout/DashboardLayout";
import SettingsLayout from "./app/settings/layout";
import AnalyticsLayout from "./app/analytics/layout";
import AccountLayout from "./app/account/layout";
import ProfitAndLossPage from "./app/analytics/profit-and-loss/page";
import ProfitCategoryPage from "./app/analytics/profit-by-category/page";
import ProfitBySkuPage from "./app/analytics/profit-by-sku/page";
import ProfitBySkuIdPage from "./app/analytics/profit-by-sku/[id]/page";
import ProfitByAsinPage from "./app/analytics/profit-by-asin/page";
import ProfitByAsinIdPage from "./app/analytics/profit-by-asin/[id]/page";
import ProfitByCategoryPage from "./app/analytics/profit-by-category/[category]/page";
import Dashboard from "./app/dashboard/DashboardPage/DashboardPage";
import InventoryPage from "./app/inventory/InventoryPage/InventoryPage";
import SettingsDetailsPage from "./app/settings/details/page";
import SettingsPrintingPage from "./app/settings/printing/page";
import SettingsProfilePage from "./app/settings/profile/page";
import SettingsTeamPage from "./app/settings/team/page";
import VariableSKUPage from "./app/settings/variable_sku/page";
import FbaCreatePage from "./app/fba/FbaCreatePage/FbaCreatePage";
import FbaShipmentFlow from "./app/fba/FbaShipmentFlow";
import FbaListingsCreatePage from "./app/fba-listings/FbaListingsCreatePage";
import FbmListingsCreatePage from "./app/fbm-listings/FbmListingsCreatePage/FbmListingsCreatePage";
import ExternalShipmentPage from "./app/shipments/ExternalShipmentPage/ExternalShipmentPage";
import ShipmentsPage from "./app/shipments/ShipmentsPage/ShipmentsPage";
import RootLayout from "./components/Rootlayout/RootLayout";
import ProfitOverviewPage from "./app/analytics/profit-overview/page";
import AdminChartsPage from "./app/admin/AdminChartsPage/AdminChartsPage";
import AdminErrorsPage from "./app/admin/AdminErrorsPage";
import IntegrationPage from "./app/settings/integration/page";
import MorePage from "./app/more/page";
import { ProductResearchPage } from "./app/product-research/ProductResearchPage/ProductResearchPage";
import BillingRecurlyPage from "./app/settings/billing-recurly/page";
import PageNotFound from "./app/PageNotFound";
import NewVerifyEmailPage from "./app/auth/(login)/new-verify-email/new-verify-email.page";
import { DraggingState, oneTimeDragNDropInit } from "./app/fba/FbaShipmentFlow/FbaShipmentFlow/DragNDrop";
import { API2 } from "@/lib/api";

declare global {
  interface Window {
    LogRocket: any;
    Intercom: any;
  }
}

declare const GIT_SHORTHASH: string; // git-shorthash of built application
declare const BUILT_AT: string; // ISO-8601 timestamp of the time this build was generated 

const globalDraggingState: DraggingState = {isDragging: false};
oneTimeDragNDropInit(globalDraggingState);

API2.get<any>("/health").then(function(result) {
  console.log("New backend is working. I wrote this 9:04am EST June 27 2025", result);
}).catch(function(error) {
  console.log("New backend is failing. I wrote this 9:04am EST June 27 2025", error);
});
API2.get<any>("/test/whoami").then(function(result) {
  console.log("New backend auth health is working. I wrote this 9:04am EST June 27 2025", result);
}).catch(function(error) {
  console.log("New backend auth health is failing. I wrote this 9:04am EST June 27 2025", error);
});

const App = () => (
  <RootLayout>
    <Switch>
      <Route path="/auth/forgot-password">
        <LoginLayout>
          <ForgotPasswordPage />
        </LoginLayout>
      </Route>
      <Route path="/auth/login">
        <LoginLayout>
          <LoginPage />
        </LoginLayout>
      </Route>
      <Route path="/auth/reset-email-sent">
        <LoginLayout>
          <ResetEmailSentPage />
        </LoginLayout>
      </Route>
      <Route path="/auth/reset-password">
        <LoginLayout>
          <ResetPasswordPage />
        </LoginLayout>
      </Route>

      <Route path="/auth/signup">
        <SignupPage />
      </Route>
      <Route path="/auth/verify-email">
        <NewVerifyEmailPage />
      </Route>
      <Route path="/auth/payment-information">
        <PaymentInformationPage />
      </Route>

      <Route path="/auth/signup/assistants">
        <AssistantPage />
      </Route>

      <Route path="/connect/seller-api" component={ConnectSellerApiPage} />
      <Route path="/connect/seller-api/succeed" component={SellerApiConnectSucceedPage} />

      <Route path="/admin" nest>
        <AdminLayout>
          <Switch>
            <Route path="/dashboard" component={AdminDashboardPage} />
            <Route path="/users" component={AdminUsersPage} />
            <Route path="/charts" component={AdminChartsPage} />
            <Route path="/errors" component={AdminErrorsPage} />
          </Switch>
        </AdminLayout>
      </Route>

      <Route>
        <DashboardLayout>
          <Switch>
            <Route path="/analytics" nest>
              <AnalyticsLayout>
                <Switch>
                  <Route path="/profit-overview" component={ProfitOverviewPage} />
                  <Route path="/profit-loss" component={ProfitAndLossPage} />
                  <Route path="/profit-by-category" component={ProfitCategoryPage} />
                  <Route path="/profit-by-category/:category" component={ProfitByCategoryPage} />
                  <Route path="/profit-by-sku" component={ProfitBySkuPage} />
                  <Route path="/profit-by-sku/:id" component={ProfitBySkuIdPage} />
                  <Route path="/profit-by-asin" component={ProfitByAsinPage} />
                  <Route path="/profit-by-asin/:id" component={ProfitByAsinIdPage} />
                </Switch>
              </AnalyticsLayout>
            </Route>
            <Route path="/" component={Dashboard} />
            <Route path="/inventory" component={InventoryPage} />
            <Route path="/settings" nest>
              <SettingsLayout>
                <Switch>
                  <Route path="/billing" component={BillingRecurlyPage} />
                  <Route path="/details" component={SettingsDetailsPage} />
                  <Route path="/printing" component={SettingsPrintingPage} />
                  <Route path="/profile" component={SettingsProfilePage} />
                  <Route path="/team" component={SettingsTeamPage} />
                  <Route path="/variable_sku" component={VariableSKUPage} />
                  <Route path="/integration" component={IntegrationPage} />
                </Switch>
              </SettingsLayout>
            </Route>
            <Route path="/account" nest>
              <AccountLayout>
                <Switch>
                  <Route path="/billing" component={BillingRecurlyPage} />
                  <Route path="/profile" component={SettingsProfilePage} />
                  <Route path="/team" component={SettingsTeamPage} />
                  <Route path="/billing-recurly" component={BillingRecurlyPage} />
                </Switch>
              </AccountLayout>
            </Route>
            <Route path="/more" component={MorePage} />
            <Route path="/fba/shipments/create" component={FbaCreatePage} />
            <Route path="/fba/shipments/:batchId">
              <FbaShipmentFlow draggingState={globalDraggingState} />
            </Route>
            <Route path="/fba/shipments/:batchId/*">
              {(params: {batchId: string}) => <Redirect to={`/fba/shipments/${params.batchId}`} replace />}
            </Route>

            <Route path="/fba/listings/create">
              <FbaListingsCreatePage />
            </Route>

            <Route path="/fbm/listings/create">
              <FbmListingsCreatePage />
            </Route>

            <Route path="/shipments/external/:shipmentId" component={ExternalShipmentPage} />
            <Route path="/shipments" component={ShipmentsPage} />

            <Route path="/product-research">
              <ProductResearchPage/>
            </Route>

            {/* important to place at the end so it is only matched if no other routes match */}
            <Route component={PageNotFound} />
          </Switch>
        </DashboardLayout>
      </Route>
    </Switch>
    <BoxiTemp/>
  </RootLayout>
);

import {useUserContext} from "@/contexts/UserContext";
import { useState } from "react";
import Flex from "@/components/Flex/Flex";
import TextInput from "@/components/forms/TextInput/TextInput";
function BoxiTemp(props: any) {
  const {user} = useUserContext();
  const [showBoxi, setShowBoxi] = useState<boolean>(false);
  const [boxiInput, setBoxiInput] = useState<string>("");
  const [chat, setChat] = useState<any>([]);

  const showEm: boolean = (user && user.email.includes("boxem.com"));
  if(!showEm) return (<></>);

  for(const e of document.querySelectorAll("#boxiTempButton")) e.remove();
  const b = document.createElement("button");
  b.id="boxiTempButton";
  b.style.position ="absolute";
  b.style.left = "50%";
  b.style.top = "100%";
  b.style.transform = "translate(-50%,-100%)";
  b.innerText = "📦";
  b.onclick=function() {
    setShowBoxi(!showBoxi);
  }
  document.body.appendChild(b);

  async function sendMessage() {
    const newInput = boxiInput;
    setBoxiInput("");
    document.getElementById("boxiInputText").focus();
    console.log("I'm sending!");
    const headers = {
      Authorization: await API2.getAccessToken(),
      "Content-Type": "application/json",
    };
    chat.push({content: newInput, role: "user", type: "message" });
    function rescroll() {
      const boxiMsgs = document.querySelector("#boxiMsgs");
      if(boxiMsgs) {
        if(boxiMsgs.scrollTop + boxiMsgs.getBoundingClientRect().height - boxiMsgs.scrollHeight >= -100) {
          boxiMsgs.scrollTo(0, boxiMsgs.scrollHeight);
        }
      }
    }
    setChat([...chat]);
    rescroll();
    const body = JSON.stringify(chat);
    console.log(body);
    fetch(process.env.PUBLIC_API2_URL + "/boxi/hangout", {headers, method:"POST", keepalive:true, body: body}).then(async (response) => {
      const reader = response.body.getReader();
      const decoder = new TextDecoder(); // Or use a specific decoder if needed
      let receivedData = '';
      let text = "";

      function processAll() {
        while(receivedData.length) {
          if(receivedData.startsWith("data: ")) receivedData = receivedData.substring("data: ".length);
          const endMessage = "\n_END_DATA_\n";
          let end = receivedData.indexOf(endMessage);
          if(end == -1) end = receivedData.length;
          const add = receivedData.substring(0,end);
          {
            let addTo = undefined;
            if(chat.length) {
              if(chat[chat.length - 1].role == "assistant") addTo = chat[chat.length - 1];
            }
            if(!addTo) {
              chat.push({content: "", role: "assistant", type: "message"});
              addTo = chat[chat.length - 1];
            }
            addTo.content += add;
            setChat([...chat]);
          }

          receivedData = receivedData.substring(end + endMessage.length);
        }
        rescroll();
      }
      try {
        while (true) {
          const { done, value } = await reader.read();
          if(done) break;
          let chunk = decoder.decode(value, { stream: true });
          receivedData += chunk;
          processAll();
        }
      } catch(err) {console.log(err, "yeeya"); }
      processAll();

    });
  }
  console.log(chat);
  return (showBoxi &&
  <Flex direction="column" align="stretch" style={{ position:"absolute", left: "50%", top:document.querySelector("#boxiTempButton")?.getBoundingClientRect().top, transform: "translate(-50%,-100%)"}}>
    <div id="boxiMsgs" style={{padding: "0.5em", overflowX: "hidden", overflowY: "auto", resize:"none", backgroundColor: "white", width:"600px", height:"400px"}}>
      {chat.map((msg, index) => {
        return <pre dangerouslySetInnerHTML={{__html: msg.content}} key={"Boxi" + index} style={{backgroundColor: msg.role === "user" ? "aliceblue" : "white", display: "block", width: "100%", textAlign: msg.role === "user" ? "right" : "left", marginTop: "1.5em", whiteSpace: "pre-wrap"}}>
        </pre>
      })}
    </div>
    <Flex direction="row" align="stretch">
      <TextInput inputId="boxiInputText" inputAutofocus={true} onSubmit={sendMessage} xButtonToClear={true} value={boxiInput} onChange={(val) => { setBoxiInput(val)}} style={{flexGrow: "1", resize:"none"}} placeholder="✉️ Write to Boxi" />
      <button onClick={sendMessage}>Send</button>
    </Flex>
  </Flex>);
}

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(<App />);

console.info(`Boxem version: ${GIT_SHORTHASH}, built at: ${new Date(BUILT_AT).toLocaleString()}`);
